import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnBackdrop?: boolean;
  children: ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/**
 * Accessible modal dialog that renders into a top-level portal, traps focus
 * via the native `<dialog>` element, and closes on Escape or backdrop click.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  children,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <dialog
      ref={ref}
      aria-labelledby={title !== undefined ? titleId : undefined}
      aria-label={title === undefined ? 'Dialog' : undefined}
      className={cn(
        'fixed left-1/2 top-1/2 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2',
        'rounded-2xl p-0 shadow-2xl backdrop:bg-gray-900/60 dark:backdrop:bg-black/70',
        'border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900',
        SIZE_CLASSES[size],
      )}
      onClick={(e) => {
        if (!closeOnBackdrop) return;
        if (e.target === ref.current) onClose();
      }}
    >
      <div
        className="flex max-h-[85vh] flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {(title !== undefined || description !== undefined) && (
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-white/10">
            <div className="min-w-0">
              {title !== undefined && (
                <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">
                  {title}
                </h2>
              )}
              {description !== undefined && (
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              aria-label="Close dialog"
              onClick={onClose}
              className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm text-gray-700 dark:text-gray-200">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-white/10">
            {footer}
          </footer>
        )}
      </div>
    </dialog>,
    document.body,
  );
}
